/**
 * Santa Clara County (California) official results: the County of Santa Clara Department of Environmental Health's food facility
 * inspection data, published on the county's Socrata portal (data.sccgov.org) in the Yelp LIVES 2.0 shape as four tables:
 * businesses (vuw7-jmjk), inspections (2u2d-8jej), violations (wkaa-4ccv) and feed_info (itys-r3sp). The sync is
 * services/api/src/official/scc-sync.ts; docs/OFFICIAL-RESULTS.md §11.
 *
 * MEASURED 2026-10-05 (about 30 requests, at least 1.3 s apart; robots.txt asks `Crawl-delay: 1`):
 *  - 9,729 businesses, 23,490 inspections (dates 20241006 to 20261002, YYYYMMDD text), 65,614 violations; every inspection's
 *    business_id is in the businesses table and every violation's inspection_id in the inspections table (0 orphans);
 *  - the inspections table spells its id column "inpsection_id" (we ask for it `as inspection_id`); its `description` column is
 *    always empty; its `inspection_comment` names inspectors ("Joint inspection with …"), so we never ask for it;
 *  - `result` is G, Y or R (the county posts "colored placards (green, yellow, red)"); `score` is present on routine inspections
 *    only (19,084 of 23,490 rows); a routine inspection with no violations listed scored 100 in 3,845 of 3,846 cases, and the
 *    mean score falls about 3 points per violation listed, so higher is better (DERIVED from the data);
 *  - the dataset metadata (`/api/views/<id>.json`) has no license field for any of the four tables and credits the "Department of
 *    Environmental Health"; the county's Terms of Use page answers 403 to our reader (a bot check), so it is unread;
 *  - Socrata ignores the weak ETag it sends (If-None-Match answered 200) and honors If-Modified-Since (304): we send only that.
 *  - It is a rolling two-year window. We KEEP inspections that age out of it (our history grows past the window); an inspection
 *    that disappears while its date is still inside the window was withdrawn by the county, and is dropped.
 *
 * PRIVACY: a violation comment is the inspector's free text. We keep it, after `scrubComment`: e-mail addresses and phone numbers
 * are replaced, and likely personal names after a role word (employee, manager, PIC, person in charge, owner, inspector…) become
 * "[name]". Inspector names are never stored: the inspections' comment column is never fetched, and names after "Specialist" or
 * "inspector" in a violation comment are replaced the same way. The business phone (a business number) is kept.
 */
import type { OfficialSource } from "./official-sources.ts";
import { publicPhone, tidyName, type FingerprintField, type OfficialCategory, type OfficialInspection, type OfficialPlaceRecord, type OfficialScale, type OfficialViolation } from "./official.ts";
import { PAS_CODE_FDA } from "./official-pasadena.ts";

export const SCC_HOST = "https://data.sccgov.org";
export const SCC_TABLES = {
  businesses: { id: "vuw7-jmjk", select: "business_id,name,address,city,postal_code,latitude,longitude,phone_number" },
  // The source's own spelling of its id column ("inpsection_id"), renamed in the query so the rest of the code reads one name.
  inspections: { id: "2u2d-8jej", select: "business_id,inpsection_id as inspection_id,date,score,result,type" },
  violations: { id: "wkaa-4ccv", select: "inspection_id,code,description,critical,violation_comment" },
} as const;
export type SccTable = keyof typeof SCC_TABLES;
export const SCC_TABLE_ORDER: readonly SccTable[] = ["businesses", "inspections", "violations"];
export const sccResourceUrl = (t: SccTable) => `${SCC_HOST}/resource/${SCC_TABLES[t].id}.csv`;
/** One page of one table, in a stable order (Socrata's row id), `pageSize` rows. */
export function sccPageUrl(t: SccTable, page: number, pageSize: number): string {
  const u = new URL(sccResourceUrl(t));
  u.searchParams.set("$select", SCC_TABLES[t].select);
  u.searchParams.set("$order", ":id");
  u.searchParams.set("$limit", String(pageSize));
  u.searchParams.set("$offset", String(page * pageSize));
  return u.toString();
}
/**
 * The schema fingerprint per table: the columns we ask for (a renamed one makes Socrata answer 400 "no-such-column", which the sync
 * also reads as a changed shape), their types in a sample, and the ones every row must have. The date is checked on its own
 * (`sccDateProblems`): YYYYMMDD text, which the shared date check does not accept.
 */
export const SCC_FINGERPRINT: Readonly<Record<SccTable, readonly FingerprintField[]>> = {
  businesses: [
    { name: "business_id", type: "text", required: true }, { name: "name", type: "text", required: true }, { name: "address", type: "text" }, { name: "city", type: "text" },
    { name: "postal_code", type: "text" }, { name: "latitude", type: "number" }, { name: "longitude", type: "number" }, { name: "phone_number", type: "text" },
  ],
  inspections: [
    { name: "business_id", type: "text", required: true }, { name: "inspection_id", type: "text", required: true }, { name: "date", type: "text", required: true },
    { name: "score", type: "number" }, { name: "result", type: "text" }, { name: "type", type: "text", required: true },
  ],
  violations: [
    { name: "inspection_id", type: "text", required: true }, { name: "code", type: "text", required: true }, { name: "description", type: "text" },
    { name: "critical", type: "text", required: true }, { name: "violation_comment", type: "text" },
  ],
};
/** The checks the shared fingerprint can't make: the inspection date is YYYYMMDD, and `critical` is true or false (more than 20% off in a sample = changed). */
export function sccDateProblems(table: SccTable, sample: readonly Record<string, string>[]): string[] {
  if (!sample.length) return [];
  const bad = (f: (r: Record<string, string>) => boolean) => sample.filter((r) => !f(r)).length;
  if (table === "inspections") { const n = bad((r) => !!sccDate(r.date ?? "")); if (n > sample.length * 0.2) return [`column "date" is no longer a YYYYMMDD date (${n} of ${sample.length} sampled rows)`]; }
  if (table === "violations") { const n = bad((r) => /^(true|false)$/i.test((r.critical ?? "").trim())); if (n > sample.length * 0.2) return [`column "critical" is no longer true or false (${n} of ${sample.length} sampled rows)`]; }
  return [];
}
export const SCC_PAGE_SIZE = 10_000;
/** The code's own least gap between two request starts: robots.txt asks `Crawl-delay: 1`; a longer crawl-delay makes it longer. */
export const SCC_FLOOR_MS = 1_000;
export const SCC_SCALE_ID = "CA/santa-clara:score";
/** The county's per-facility page (the coordinator's pattern, 2026-10-05). The site is behind a bot check, so it is linked, never fetched. */
export const SCC_RECORD_BASE = "https://eservices.sccgov.org/FacilityInspection/Home/ShowDetail/";
/** Every business_id in the data is "PR" and 7 digits (9,729 of 9,729, MEASURED); anything else gets the county's lookup instead. */
export const SCC_ID_RE = /^PR\d{7}$/;
/** The placarding program page (the packs' lookup entry): it says results "can be viewed on the County website or with the mobile app". */
export const SCC_LOOKUP = "https://deh.santaclaracounty.gov/food-and-retail/compliance-retail-food-operations/food-facility-placarding-and-scoring-program";
export const SCC_METADATA_URL = `${SCC_HOST}/api/views/2u2d-8jej.json`;

export const SCC_SOURCES: readonly OfficialSource[] = [
  {
    // Names from the pack's node (CA/santa-clara: "County of Santa Clara Department of Environmental Health", Santa Clara County).
    id: "sccgov", state: "CA", jurisdiction: "CA/santa-clara", department: "", area: "", status: "sync",
    format: "sccgov_lives", urls: SCC_TABLE_ORDER.map(sccResourceUrl), minGapMs: SCC_FLOOR_MS, pageSize: SCC_PAGE_SIZE,
    parser: "sccgov", categories: true, updates: "by the county on its open data site (we check monthly)",
    // Shown (owner, 2026-10-05: show all counties) once the tests pass.
    displayApproved: true,
    basis: {
      kind: "facts",
      terms: "public records the County publishes on its open data portal; the dataset metadata states no license and credits the Department of Environmental Health; facts are not copyrightable (Feist v. Rural, 499 U.S. 340)",
      // The metadata's own attribution field (it has no license field: services/api/test/fixtures/sccgov/metadata.json).
      quote: "Department of Environmental Health", url: SCC_METADATA_URL,
    },
    flag: { clause: "No license in the dataset metadata; the county Terms of Use page answers 403 (unread)", url: "https://home.sccgov.org/terms-of-use" },
    attribution: "County of Santa Clara Department of Environmental Health, data.sccgov.org",
    lookupUrl: SCC_LOOKUP,
    recordHint: "Opens the county's own page for this place.",
    scaleLine: {
      line: "Each inspection gets a green, yellow or red placard, and routine inspections a score. In the data, 100 means no violations were listed, and the score drops as violations are found, so higher is better. The county's page does not list which scores go with each color.",
      citation: "County of Santa Clara Department of Environmental Health, Food Facility Placarding and Scoring Program (read 2026-10-02)",
      url: SCC_LOOKUP,
    },
  },
];

export const SCC_SCALE: OfficialScale = {
  id: SCC_SCALE_ID, kind: "score_higher_better", unit: "", noun: "points taken off", lowerIsBetter: false, min: 50, max: 100,
  meaning: "The county posts a green, yellow or red placard after each inspection, and gives routine inspections a score. In the county's data, a routine inspection with no violations listed scored 100, and the score drops as violations are found. The county's page does not say which scores or findings give each color: ask the department.",
  source: {
    citation: "County of Santa Clara DEH: \"posting colored placards (green, yellow, red) at food facilities\" (read 2026-10-02); the score's direction from its data (data.sccgov.org, 2u2d-8jej and wkaa-4ccv, 2026-10-05)",
    url: SCC_LOOKUP,
  },
};

/** The county's placard letters → its own color words. Anything else is not shown as a color. */
export const SCC_PLACARD: Readonly<Record<string, string>> = { G: "Green", Y: "Yellow", R: "Red" };

/** "20261002" → "2026-10-02"; anything else (another format, a month 13, Feb 30) → "". */
export function sccDate(v: string): string {
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(v.trim());
  if (!m) return "";
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = new Date(Date.UTC(y, mo - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d ? `${m[1]}-${m[2]}-${m[3]}` : "";
}

/** The data names no kind of place: only what the name says (a restaurant stays "other", so typical ranges use all food places). */
export function sccCategory(name: string): OfficialCategory {
  if (/\b(coffee|espresso)\b/i.test(name)) return "coffee";
  if (/\b(market|grocery|supermarket)\b/i.test(name)) return "market";
  return "other";
}
/** A microenterprise home kitchen is someone's home: city only (core isCityOnlyKind's rule, by the name, since the data has no type column). */
const HOME_KITCHEN = /\bmehko\b|micro ?enterprise home kitchen/i;

export const sccRecordUrl = (businessId: string) => (SCC_ID_RE.test(businessId) ? `${SCC_RECORD_BASE}${businessId}` : SCC_LOOKUP);

// ── Privacy: the inspector's free text ─────────────────────────────────────────────────────────────────────────────

/** A role word a person's name may follow ("the manager, Bruce, …", "PIC Jun Wei stated", "Specialist Monica H."). Case-insensitive. */
const ROLE = String.raw`\b(?:(?:assistant|store|kitchen|general|facility|new|shift)\s+)?(?:employees?|managers?|pic|(?:persons?|people)\s+in\s+charge(?:\s+during\s+(?:the\s+)?inspection)?|owners?(?:\/managers?)?|operators?|cooks?|chefs?|staff|workers?|supervisors?\s+(?:notified|contacted)(?:\s+of\s+(?:the\s+)?closure)?|supervisors?|cashiers?|servers?|dishwashers?|(?:supervising\s+)?(?:environmental\s+health\s+)?specialists?|inspectors?|rehs|notification|(?:food\s+safety\s+)?certificates?(?:\s+holder)?)\b`;
/** One or two name words: "Bruce", "Jun Wei", "Robert Cheung", "Jessica Z.", "Mary-Ann". Case-sensitive. */
const NAME = String.raw`[A-Z][a-z]+(?:[-'][A-Za-z]+)?(?:[ \t]+(?:[A-Z][a-z]+(?:[-'][A-Za-z]+)?|[A-Z]\.?(?![A-Za-z]))){0,2}`;
/** What follows a name in these comments: punctuation, or a word that starts the rest of the sentence. */
const AFTER = String.raw`(?=\s*(?:[,.;:)(&\[]|$)|\s+[-–\d]|\s+(?:and|who|via|on|over|at|stated|states|said|says|was|is|has|had|have|explained|indicated|mentioned|confirmed|agreed|informed|notified|discarded|provided|will|did|that|she|he|they|by|from|to|for|with|about|of|were|are)\b)`;
const ROLE_RE = new RegExp(ROLE, "gi");
const AFTER_ROLE_RE = new RegExp(String.raw`^\s*(?:[,:]\s*)?(?:named\s+|name\s+is\s+)?(?:Chef\s+)?(${NAME})${AFTER}`);
const AND_NAME_RE = new RegExp(String.raw`^\s*,?\s*(?:and|&)\s+(${NAME})${AFTER}`);
const BEFORE_ROLE_RE = new RegExp(String.raw`\b(${NAME})\s*\(([^)]{1,40})\)`, "g");
const ROLE_ONLY_RE = new RegExp(ROLE, "i");
const HONORIFIC_RE = new RegExp(String.raw`\b(?:Mr|Mrs|Ms|Miss|Dr)\.?\s+(${NAME})`, "g");
/**
 * Capitalized words that follow a role word in these comments and are not names (MEASURED on the 2026-10-05 pull: "employee Food
 * Handler's Card", "PIC Voluntarily Condemned", "Inspector Observations:", "Per PIC, Time as a public health control").
 */
const NOT_NAMES = new Set(("food time voluntarily the this that these those it its they he she we i you hot cold raw cooked water per note notes all facility facilities certificate certification "
  + "certified handler handlers safety corrective action observations observation repeat on in at for to and or a an no not please ensure provide obtain lack discussed spoke called was is has had will did "
  + "stated said informed environmental health specialist specialists supervising department county deh inspector inspection inspections translation notification person charge manager managers "
  + "owner owners employee employees staff pic operator california ca sa cos tphc phf phfs servsafe ecolab also both each every some any many most one two three there here when after before during "
  + "onsite site verified confirmed his her their our my new current valid expired approved proper kitchen bar front back dining restaurant store market cafe coffee ice sushi rice chicken meat beef "
  + "pork fish egg eggs milk cheese sauce soup salad sandwich sandwiches dough bread hand hands handwash sink sinks gloves glove card cards permit plan review mr mrs ms dr monday tuesday wednesday "
  + "thursday friday saturday sunday january february march may july september october november december if then upon while since until as of with by from about only some "
  + "yes notified chef me building conveyor oven encore bullet unagi pho exam base autochlor eggplant vegies sausage lau eco observed see couple presented turbo ventless fryer").split(/\s+/));
const isStop = (w: string) => { const x = w.replace(/[.'-].*$/, "").toLowerCase(); return x.length >= 2 && NOT_NAMES.has(x); };
/** The name as found, without trailing words that are not names ("Lew Notified" → "Lew"), or "" when its first word is not a name. */
const cleanName = (n: string) => { const w = n.trim().split(/\s+/); while (w.length > 1 && isStop(w.at(-1)!)) w.pop(); return w.every((x) => !isStop(x)) && /[a-z]/.test(w[0]!) ? w.join(" ") : ""; };
/** "Supervisor Notified: Elizabeth tobin." (the closure template): the rest of the line is the supervisor's name unless it says yes or no. */
/** The closure template: "The person in charge during inspection, <names>, has been informed…": that slot always holds people (even "My" or "Pho"). */
const TEMPLATE_RE = /\bin\s+charge\s+during\s+(?:the\s+)?inspection\s*,?\s*([^,\n]{1,60}?)\s*,?\s*(?:has|have|hav|had|were|was|is|are)\s+been\s+(?:informed|notified)/gi;
const SUPERVISOR_LINE_RE = /\bsupervisor\s+notified\s*:\s*((?:[A-Z]\.\s*)?[^\n.;\[\]]{1,40})/gi;

export const SCRUB = { name: "[name]", email: "[email]", phone: "[phone]" } as const;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@\s?[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g;
const PHONE_RE = /(?:\+?1[\s.-]?)?(?:\(\d{3}\)\s?|\b\d{3}[\s.-])\d{3}[\s.-]\d{4}\b|\+1\d{10}\b/g;
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The likely personal names in a comment (conservative: only right after a role word, in parentheses before one, or after Mr/Ms). */
export function likelyNames(text: string): string[] {
  const found = new Set<string>();
  for (const m of text.matchAll(ROLE_RE)) {
    const rest = text.slice(m.index! + m[0].length, m.index! + m[0].length + 120);
    const m1 = AFTER_ROLE_RE.exec(rest);
    const n1 = m1 ? cleanName(m1[1]!) : "";
    if (!n1) continue;
    found.add(n1);
    // "…during inspection, Jane Doe and Sam (over the phone), have been informed": the names joined to the first one.
    let tail = rest.slice(m1![0].length);
    for (let k = 0; k < 3; k++) {
      const m2 = AND_NAME_RE.exec(tail);
      const n2 = m2 ? cleanName(m2[1]!) : "";
      if (!n2) break;
      found.add(n2); tail = tail.slice(m2![0].length);
    }
  }
  for (const m of text.matchAll(BEFORE_ROLE_RE)) { const n = cleanName(m[1]!); if (n && ROLE_ONLY_RE.test(m[2]!)) found.add(n); }
  for (const m of text.matchAll(HONORIFIC_RE)) { const n = cleanName(m[1]!); if (n) found.add(n); }
  for (const m of text.matchAll(TEMPLATE_RE)) { const n = m[1]!.trim(); if (/^[A-Z]/.test(n) && n !== SCRUB.name) found.add(n); }
  for (const m of text.matchAll(SUPERVISOR_LINE_RE)) { const n = m[1]!.replace(/\s+\d+\s*$/, "").trim(); if (!/^(yes|no|n\/?a|none)\b/i.test(n) && /^[A-Z]/.test(n)) found.add(n); }
  // A lone initial ("A.") is never replaced on its own: it would hit every "A" in the comment.
  return [...found].filter((n) => n.replace(/[^A-Za-z]/g, "").length >= 2);
}

/**
 * The inspector's comment as we store it: e-mail addresses → "[email]", phone numbers → "[phone]", likely personal names →
 * "[name]" (each name, once found, everywhere in that comment: "the manager, Gabby. Gabby stated…"). `flagged` = names were found.
 */
export function scrubComment(raw: string): { text: string; flagged: boolean; names: number; emails: number; phones: number } {
  let emails = 0, phones = 0;
  let t = raw.replace(EMAIL_RE, () => { emails++; return SCRUB.email; }).replace(PHONE_RE, () => { phones++; return SCRUB.phone; });
  const names = likelyNames(t);
  // Whole phrases first ("Robert Cheung", "Jessica Z."), then each word of two letters or more on its own.
  // A word found in a name is replaced everywhere in the comment when it is capitalized, two letters or more, and not a common word.
  const words = new Set(names.flatMap((n) => n.split(/[\s/&]+/)).map((w) => w.replace(/[.,]$/, "")).filter((w) => /^[A-Z][a-z]/.test(w) && !isStop(w)));
  for (const n of [...names].sort((a, b) => b.length - a.length)) t = t.replace(new RegExp(`(?<![A-Za-z])${esc(n)}(?![A-Za-z])`, "g"), SCRUB.name);
  for (const w of words) t = t.replace(new RegExp(`(?<![A-Za-z])${esc(w)}(?![A-Za-z])`, "g"), SCRUB.name);
  return { text: t, flagged: names.length > 0, names: names.length, emails, phones };
}

// ── Codes → the five risk factors ─────────────────────────────────────────────────────────────────────────────────

/**
 * Santa Clara County's violation codes → FDA Form 3-A items. The codes are the California retail food inspection form's items, the
 * same form Pasadena's older records use with three digits (SCC "K07" = Pasadena "K007"; every shared code's title matches: the
 * pairs are pinned in services/api/test/scc.test.ts against fixtures/sccgov/codes-seen.tsv). So the shared codes reuse Pasadena's
 * table (core official-pasadena.ts, matched by title). Codes Pasadena never saw are matched here by TITLE to Form 3-A items
 * (core inspectionform.ts), DERIVED 2026-10-05:
 *   K02 "Communicable disease; reporting/restriction/exclusion" → 3, 4; K03 "No discharge from eyes, nose, mouth" → 7;
 *   K10 "Proper cooking time & temperatures" → 18; K11 "Proper reheating procedures for hot holding" → 19 (not one of the five, D-O6);
 *   K12 "Returned and reservice of food" → 17; K15 "Food obtained from approved source" → 11;
 *   K18 "Compliance with variance/ROP/HACCP Plan" → 29; K20 "…prohibited foods not being offered" → 26; K28 "Fruits and vegetables washed" → 42;
 *   K01 "Demonstration of knowledge; food safety certification" → 1, 2 (Pasadena: 1; both are other public health items).
 * Not mapped (never "clear"): K31 consumer self service, K37 vending machines (no one Form 3-A title), K47 signs, K48 plan review,
 * K49 permits, K58 placard posted (paperwork), and any code not seen yet.
 */
const SCC_ONLY: Readonly<Record<string, readonly number[]>> = { K01: [1, 2], K02: [3, 4], K03: [7], K10: [18], K11: [19], K12: [17], K15: [11], K18: [29], K20: [26], K28: [42] };
const SHARED_WITH_PASADENA = ["K04", "K05", "K06", "K07", "K08", "K09", "K13", "K14", "K16", "K19", "K21", "K22", "K23", "K24", "K25", "K26", "K27", "K29", "K30", "K32", "K33", "K34", "K35", "K36", "K38", "K39", "K40", "K41", "K42", "K43", "K44", "K45", "K46"];
/** "K07" → "K007" (Pasadena's spelling of the same item). */
export const pasCodeOf = (scc: string) => scc.replace(/^K(\d{2})$/, "K0$1");
export const SCC_CODE_FDA: Readonly<Record<string, readonly number[]>> = {
  ...Object.fromEntries(SHARED_WITH_PASADENA.map((c) => [c, PAS_CODE_FDA[pasCodeOf(c)]!])),
  ...SCC_ONLY,
};

// ── The join: businesses → inspections → violations ──────────────────────────────────────────────────────────────

type Row = Record<string, string>;
const num = (v: string | undefined): number | null => (v === undefined || v.trim() === "" || !Number.isFinite(Number(v)) ? null : Number(v));

/** Counts from one run, for the run's note and the review. */
export interface SccStats { businesses: number; inspections: number; violations: number; badDates: number; inspectionsWithoutBusiness: number; violationsWithoutInspection: number; commentsFlagged: number; emails: number; phones: number }

/**
 * Joins the three tables, fed in order (all businesses, then all inspections, then all violations; each in pages). Memory: one
 * small object per business and per inspection, and each violation's scrubbed comment (about 20 MB of text for the whole feed).
 */
export class SccJoin {
  private places = new Map<string, OfficialPlaceRecord["place"]>();
  private inspections = new Map<string, OfficialInspection>();
  private byPlace = new Map<string, OfficialInspection[]>();
  readonly stats: SccStats = { businesses: 0, inspections: 0, violations: 0, badDates: 0, inspectionsWithoutBusiness: 0, violationsWithoutInspection: 0, commentsFlagged: 0, emails: 0, phones: 0 };
  /** The oldest inspection date in this run's data: an inspection we hold that is older aged out of the window. */
  windowStart = "";
  constructor(private src: OfficialSource) {}

  add(table: SccTable, rows: readonly Row[]): void {
    if (table === "businesses") for (const r of rows) this.business(r);
    else if (table === "inspections") for (const r of rows) this.inspection(r);
    else for (const r of rows) this.violation(r);
  }

  private business(r: Row): void {
    const id = (r.business_id ?? "").trim();
    if (!id) return;
    this.stats.businesses++;
    const name = tidyName(r.name ?? "");
    const home = HOME_KITCHEN.test(name);
    const lat = num(r.latitude), lon = num(r.longitude);
    const phone = publicPhone(r.phone_number ?? "");
    const s = this.src;
    this.places.set(id, {
      key: `${s.id}:${id}`, sourceId: s.id, jurisdiction: s.jurisdiction, area: s.area, extId: id, name,
      address: home ? "" : tidyName(r.address ?? ""), city: tidyName(r.city ?? ""), zip: home ? "" : (r.postal_code ?? "").trim().slice(0, 5),
      ...(!home && phone ? { phone } : {}), ...(!home && lat !== null && lon !== null && lat !== 0 && lon !== 0 ? { geo: { lat, lon }, geoSource: "source" as const } : {}),
      ...(home ? { privacy: "city_only" as const } : {}),
      sourceCategory: "", category: sccCategory(name), recordUrl: sccRecordUrl(id), recordHint: s.recordHint, department: s.department,
      dataUrl: `${SCC_HOST}/resource/${SCC_TABLES.inspections.id}.json?business_id=${encodeURIComponent(id)}`,
    });
  }

  private inspection(r: Row): void {
    const id = (r.inspection_id ?? "").trim(), biz = (r.business_id ?? "").trim();
    if (!id) return;
    const place = this.places.get(biz);
    if (!place) { this.stats.inspectionsWithoutBusiness++; return; }
    const date = sccDate(r.date ?? "");
    if (!date) { this.stats.badDates++; return; }
    this.stats.inspections++;
    if (!this.windowStart || date < this.windowStart) this.windowStart = date;
    const type = (r.type ?? "").trim();
    const color = SCC_PLACARD[(r.result ?? "").trim().toUpperCase()] ?? null;
    const i: OfficialInspection = {
      placeKey: place.key, extId: id, date, score: num(r.score), grade: color ? `${color} placard` : null, result: color, scaleId: SCC_SCALE_ID,
      type: type ? tidyName(type) : null, routine: /^routine inspection$/i.test(type),
      violations: { count: 0, critical: 0, items: [], codes: [] }, recordUrl: place.recordUrl,
    };
    this.inspections.set(id, i);
    const list = this.byPlace.get(place.key);
    if (list) list.push(i); else this.byPlace.set(place.key, [i]);
  }

  private violation(r: Row): void {
    const i = this.inspections.get((r.inspection_id ?? "").trim());
    if (!i) { this.stats.violationsWithoutInspection++; return; }
    const code = (r.code ?? "").trim().toUpperCase();
    if (!code) return;
    this.stats.violations++;
    const critical = /^true$/i.test((r.critical ?? "").trim());
    const s = scrubComment(r.violation_comment ?? "");
    if (s.flagged) this.stats.commentsFlagged++;
    this.stats.emails += s.emails; this.stats.phones += s.phones;
    // The CDP convention (core official-records.ts): `rationale` is the code's own title, `text` the inspector's line for this visit.
    const v: OfficialViolation = { code, text: s.text.trim(), critical, rationale: (r.description ?? "").replace(/\s+/g, " ").trim() };
    i.violations.count++; if (critical) i.violations.critical++;
    i.violations.codes!.push(code);
    i.violations.items.push(v);
  }

  /** Every business with at least one inspection, its inspections newest first. */
  records(fetchedAt: string, hash: string): OfficialPlaceRecord[] {
    const out: OfficialPlaceRecord[] = [];
    for (const [key, list] of this.byPlace) {
      const place = this.places.get(key.slice(this.src.id.length + 1))!;
      out.push({ place, inspections: [...list].sort(byNewest), provenance: { sourceId: this.src.id, fetchedAt, hash } });
    }
    return out.sort((a, b) => (a.place.key < b.place.key ? -1 : 1));
  }
}
const byNewest = (a: OfficialInspection, b: OfficialInspection) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0);

/**
 * Keep what we hold (the feed is a rolling two-year window): the fresh record's inspections, plus each inspection we already held
 * that is no longer in the feed AND is older than the feed's oldest date (it aged out). One that vanished while its date is still
 * inside the window was withdrawn by the county: it is dropped. Same id in both: the county's current version wins.
 */
export function keepAgedOut(prev: OfficialPlaceRecord | undefined, fresh: OfficialPlaceRecord, windowStart: string): OfficialPlaceRecord {
  if (!prev) return fresh;
  const ids = new Set(fresh.inspections.map((i) => i.extId ?? `${i.date}|${i.type}`));
  const aged = prev.inspections.filter((i) => !ids.has(i.extId ?? `${i.date}|${i.type}`) && !!windowStart && i.date < windowStart);
  return aged.length ? { ...fresh, inspections: [...fresh.inspections, ...aged].sort(byNewest) } : fresh;
}

/** Every row at once (tests and the storage measurement). */
export function sccParseAll(src: OfficialSource, tables: Partial<Record<SccTable, readonly Row[]>>, fetchedAt: string, hash: string): { records: OfficialPlaceRecord[]; join: SccJoin } {
  const j = new SccJoin(src);
  for (const t of SCC_TABLE_ORDER) j.add(t, tables[t] ?? []);
  return { records: j.records(fetchedAt, hash), join: j };
}

